// The state of each "Add pages from a PDF" import's warnings, decided as the server decides it for plan generation
// (service/verbaschola_pdf_import_checks.py, whose note explains the rules). An import's block is found by
// "(import <id>)" at the end of its first line, and must be in the text exactly once. A warning is about one line of
// one page as read, and may rest on other lines (an AI finding's context); each is an anchor. An anchor whose text was
// the only such line on the page is followed where it moves; one among identical lines, and every anchor of a finding
// of missing text, holds only while the page is unchanged (its version). Nothing settles a warning but the teacher: an
// edited line is "changed", to confirm. A page's AI check covers one version of its text; edited since, it is "changed".
import type { IVerbaScholaPdfImport, IVerbaScholaPdfImportWarning, IVerbaScholaPdfPageCheck, PdfImportAnchor, PdfImportDecision } from '../../api/VerbaScholaApi';

/** How an AI finding is named, by what it rests on (the server's plan warning says the same). */
export const AI_EVIDENCE_LABELS: Record<'page' | 'language', string> = {
    page: 'Possible transcription error (the page picture shows something else)',
    language: 'Possible Chinese-language issue (it looks wrong as Chinese, even if the page prints it so)',
};

export const SOURCE_LINE_PREFIX = 'Textbook pages: ';
/** The popup's note under a page's mark (see pdfPagesBlock); not part of the page's own text. */
export const IMPORT_NOTE_PREFIX = '[PDF import note: ';
// A printed page label may hold brackets ("Unit 1 (review)"): it runs to the line's last ") =====" (as on the server).
const MARKER = /^===== PDF page (\d+)(?: \(printed [^\n]*\))? =====$/;
const IMPORT_LINE = /^Textbook pages: .*\(import (\d+)\)$/;

/** The first line of an import's block: its source line and the record's id. */
export function importLine(sourceLine: string, importId: number): string {
    return `${sourceLine} (import ${importId})`;
}

/** A page text's version: FNV-1a (32 bits) of its lines joined by newlines, as UTF-8 (the server computes the same). */
export function pageVersion(lines: string[]): string {
    let value = 0x811c9dc5;
    for (const byte of new TextEncoder().encode(lines.join('\n'))) value = Math.imul(value ^ byte, 0x01000193) >>> 0;
    return value.toString(16).padStart(8, '0');
}

/** A line of a field's text, and where it starts in the field. */
export interface FieldLine { line: string; start: number }

function fieldLines(text: string): FieldLine[] {
    let offset = 0;
    return text.split('\n').map(line => {
        const item = { line, start: offset };
        offset += line.length + 1;
        return item;
    });
}

/** Each block that starts with this import's first line, to the next import's first line or the end. */
export function importBlocks(text: string, importId: number): FieldLine[][] {
    const all = fieldLines(text);
    return all.flatMap((item, first) => {
        if (Number(IMPORT_LINE.exec(item.line)?.[1]) !== importId) return [];
        let end = all.findIndex((other, index) => index > first && other.line.startsWith(SOURCE_LINE_PREFIX));
        if (end < 0) end = all.length;
        return [all.slice(first, end)];
    });
}

/** A page's lines in its block, without its mark and the popup's note; null when its mark was removed. */
export function pageLines(block: FieldLine[], pageNumber: number): FieldLine[] | null {
    const mark = block.findIndex(item => Number(MARKER.exec(item.line)?.[1]) === pageNumber);
    if (mark < 0) return null;
    let end = block.findIndex((item, index) => index > mark && MARKER.test(item.line));
    if (end < 0) end = block.length;
    let body = block.slice(mark + 1, end);
    if (body.length && body[0].line.startsWith(IMPORT_NOTE_PREFIX)) body = body.slice(1);
    // The blank line before the next page belongs to the block's layout, not to the page.
    while (body.length && !body[body.length - 1].line.trim()) body = body.slice(0, -1);
    return body;
}

export type WarningState = 'open' | 'looks_right' | 'changed';

/** Where an anchored line is now: found (see the note above), or null; ``bound``: it holds only while the page is unchanged. */
function anchorLine(lines: FieldLine[], anchor: PdfImportAnchor, unchanged: boolean, bound: boolean): FieldLine | null {
    const atPlace = lines[anchor.index];
    if (bound || !anchor.unique) return unchanged && atPlace?.line === anchor.line ? atPlace : null;
    if (atPlace?.line === anchor.line) return atPlace;
    const matches = lines.filter(item => item.line === anchor.line);
    return matches.length === 1 ? matches[0] : null;
}

export interface WarningLines {
    state: WarningState;
    /** The warning's own line now: where it was found, or (changed) the line at its place, if any. */
    line: FieldLine | null;
    /** The same for each line it rests on. */
    context: (FieldLine | null)[];
}

/** A warning's state and lines on its page as it is now (null: its page's mark was removed). */
export function warningState(warning: IVerbaScholaPdfImportWarning, lines: FieldLine[] | null, readVersion: string): WarningLines {
    const decided = warning.decision === 'looks_right';
    // Records saved before context lines were kept have none.
    if (!lines) return { state: 'changed', line: null, context: (warning.context ?? []).map(() => null) };
    const own: PdfImportAnchor = decided
        ? { index: warning.decided_index ?? 0, line: warning.decided_line ?? '', unique: Boolean(warning.decided_unique) }
        : { index: warning.line_index, line: warning.line ?? '', unique: Boolean(warning.line_unique) };
    const context = decided ? warning.decided_context ?? [] : warning.context ?? [];
    const unchanged = pageVersion(lines.map(item => item.line)) === (decided ? warning.decided_version : warning.base_version ?? readVersion);
    const found = [own, ...context].map(anchor => anchorLine(lines, anchor, unchanged, Boolean(warning.insert)));
    const settled = found.every(Boolean);
    const place = (anchor: PdfImportAnchor, at: FieldLine | null) => at ?? lines[anchor.index] ?? null;
    return {
        state: settled ? (decided ? 'looks_right' : 'open') : 'changed',
        line: place(own, found[0]),
        context: context.map((anchor, index) => place(anchor, found[index + 1])),
    };
}

/** "Looks right" for a warning, on its lines as the teacher sees them now; null when one of them cannot be placed. */
export function lookedRight(lines: FieldLine[] | null, at: WarningLines): PdfImportDecision | null {
    if (!lines || !at.line || at.context.some(item => !item)) return null;
    const anchor = (item: FieldLine): PdfImportAnchor => ({
        index: lines.indexOf(item), line: item.line, unique: lines.filter(other => other.line === item.line).length === 1,
    });
    const own = anchor(at.line);
    return {
        decision: 'looks_right', decided_index: own.index, decided_line: own.line, decided_unique: own.unique,
        decided_context: at.context.map(item => anchor(item as FieldLine)), decided_version: pageVersion(lines.map(item => item.line)),
    };
}

export type PageCheckState = IVerbaScholaPdfPageCheck['status'] | 'changed';

/** A page's AI check: checked (its text now is what the check covered), changed (edited since), or as it was saved. */
export function aiCheckState(page: IVerbaScholaPdfImport['pages_json'][number], lines: FieldLine[] | null): PageCheckState {
    const status = page.ai_check?.status ?? 'not_checked';
    if ((status === 'checked' || status === 'partial') && (!lines || pageVersion(lines.map(item => item.line)) !== page.ai_check.version)) return 'changed';
    return status;
}

export interface ImportState {
    record: IVerbaScholaPdfImport;
    /** How many copies of its block the fields hold, and the field of the first; its warnings are placed only with one copy. */
    copies: number;
    field: IVerbaScholaPdfImport['lesson_field'] | null;
    warnings: ({ warning: IVerbaScholaPdfImportWarning; pageLines: FieldLine[] | null } & WarningLines)[];
    /** Each page, its lines now and its AI check's state (none when the block is not there exactly once). */
    pages: { page: IVerbaScholaPdfImport['pages_json'][number]; lines: FieldLine[] | null; checkState: PageCheckState }[];
}

/** Each import of the lesson with its copies in the fields and, with exactly one, each warning's state. */
export function importStates(imports: IVerbaScholaPdfImport[], texts: Record<IVerbaScholaPdfImport['lesson_field'], string>): ImportState[] {
    return [...imports].sort((a, b) => a.id - b.id).map(record => {
        const found = (['brief', 'ai_context_prompt_text'] as const).flatMap(field => importBlocks(texts[field], record.id).map(block => ({ field, block })));
        if (found.length !== 1) return { record, copies: found.length, field: found[0]?.field ?? null, warnings: [], pages: [] };
        const versions = new Map(record.pages_json.map(page => [page.page_number, pageVersion((page.read_text ?? '').split('\n'))]));
        const pages = record.pages_json.map(page => {
            const lines = pageLines(found[0].block, page.page_number);
            return { page, lines, checkState: aiCheckState(page, lines) };
        });
        return {
            record, copies: 1, field: found[0].field, pages,
            warnings: record.warnings_json.map(warning => {
                const lines = pageLines(found[0].block, warning.page_number);
                return { warning, pageLines: lines, ...warningState(warning, lines, versions.get(warning.page_number) ?? '') };
            }),
        };
    });
}
